import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { OFFER_TYPES } from "../../../../constants";
import { formatNumber, getLastOfferUpdateDatetime } from "../../../../utils";
import {
  deliveryCostAmount,
  demandCostState,
  demandTermsOf,
  hasDeliveryCost,
  latestDemandDeliveryCost,
} from "../../../../utils/delivery-cost";
import { getLocalisedName } from "../../../../utils/i18n";
import { Carousel } from "../../../Deposits/DepositCard/Carousel";
import {
  canRecallOffer,
  computeDemandRollup,
  demandLinesInPlay,
  demandPreviewPhotos,
  demandStatusBadgeVariant,
  demandTermChanges,
  demandTotals,
  formatOfferDateTime,
  formatQuantityRange,
  formatUnitPrice,
  getActiveProposal,
  isSchoolDemand,
  offerBaselineTerms,
  offerDisplayStatus,
  offerPictureUrls,
  offerStatusLabelKey,
  offerTerms,
  rollupDriverOffer,
  termsValue,
} from "../../utils";
import { DeliveryActionButtons, type DeliveryDialogKind } from "../DeliveryActionButtons";
import {
  AggregatorLine,
  BuyerLine,
  ChangeChip,
  DemandCostBadge,
  offerBriefText,
  offerCommodityTitle,
  offerCurrencySymbol,
  OfferActionBand,
  offerBandTone,
  OfferActionButton,
  OfferCardHeader,
  ReviewDeliveryCostButton,
} from "../OfferCard";
import { Offer, OfferStatus } from "../OfferCard/interfaces";
import { type DeliveryLookups } from "../../types";

interface DemandGroupCardProps {
  demandId: number;
  offers: Offer[];
  onViewComments: (offer: Offer) => void;
  onRecall: (offer: Offer) => void;
  onAggregatorDetails: (id: number) => void;
  // Opens the demand's delivery-cost negotiation; absent where the cost cannot be negotiated (read-only).
  onReviewDeliveryCost?: (demandId: number) => void;
  onDeliveryAction?: (kind: DeliveryDialogKind, offer: Offer) => void;
  deliveryLookups: DeliveryLookups;
  hideRecall?: boolean;
  showBuyer?: boolean;
  readOnly?: boolean;
}

export const DemandGroupCard = ({
  demandId,
  offers,
  onViewComments,
  onRecall,
  onAggregatorDetails,
  onReviewDeliveryCost,
  onDeliveryAction,
  deliveryLookups,
  hideRecall = false,
  showBuyer = false,
  readOnly = false,
}: DemandGroupCardProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const rollup = useMemo(() => computeDemandRollup(offers), [offers]);
  const schoolDemand = useMemo(() => isSchoolDemand(offers), [offers]);
  const totals = useMemo(() => demandTotals(offers), [offers]);
  const costTerms = useMemo(() => demandTermsOf(offers), [offers]);
  // The demand's single cost has a row once one applies: an amount, a proposal, or a cost still open.
  const showDeliveryCost =
    !!costTerms &&
    (hasDeliveryCost(costTerms.delivery_cost) ||
      !!costTerms.pending_delivery_cost_proposal ||
      !costTerms.delivery_cost_locked);
  const transportCost = useMemo(() => {
    if (!schoolDemand) return 0;
    const costs = offers.find(offer => offer.demand_costs)?.demand_costs;
    return Number(costs?.transport_cost) || 0;
  }, [offers, schoolDemand]);
  const grandTotal = totals.value + totals.deliveryCost + transportCost;
  const changes = useMemo(() => demandTermChanges(offers), [offers]);
  const driver = useMemo(() => rollupDriverOffer(offers), [offers]);
  const preview = useMemo(() => demandPreviewPhotos(offers), [offers]);
  const morePhotosLabel = t("representativeOffers.demand.morePhotos", {
    count: preview.moreLines,
  });

  const currency = offerCurrencySymbol(t);
  const tone = offerBandTone(driver);

  // Lines can carry different units (e.g. Potato in bags, Groundnut in kg), so
  // each row labels with its own unit and demand-level totals only show a unit
  // when every active line shares one.
  const activeUnits = new Set(
    demandLinesInPlay(offers)
      .map(offer => offer.unit_of_measure?.code)
      .filter((code): code is string => !!code)
  );
  const sharedUnit = activeUnits.size === 1 ? [...activeUnits][0] : undefined;
  const briefUnit = sharedUnit ?? driver?.unit_of_measure?.code ?? "";

  const updatedAt = offers.reduce((latest, offer) => {
    const value = getLastOfferUpdateDatetime(offer, getActiveProposal(offer));
    return value > latest ? value : latest;
  }, "" as string);

  const quantityLabel = (value: number, unitCode: string) =>
    unitCode ? `${formatNumber(value)} ${unitCode}` : formatNumber(value);
  const valueLabel = (value: number) => `${currency}${formatNumber(value)}`;
  const wasChip = (value: string) => (
    <span className="inline-flex w-fit rounded-full bg-warning-100 px-2 py-0.5 font-medium text-warning-800 text-xs">
      {t("representativeOffers.card.was", { value })}
    </span>
  );

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <OfferCardHeader
        leading={
          <div className="relative size-24 shrink-0">
            <Carousel className="size-full" imageUrls={preview.urls} />
            {!open && preview.moreLines > 0 && (
              <button
                type="button"
                className="absolute right-1 bottom-1 rounded-full bg-black/60 px-1.5 py-0.5 font-semibold text-white text-xs tabular-nums hover:bg-black/80"
                aria-label={morePhotosLabel}
                title={morePhotosLabel}
                onClick={() => setOpen(true)}
              >
                +{preview.moreLines}
              </button>
            )}
          </div>
        }
        reference={t(
          schoolDemand
            ? "representativeOffers.demand.schoolTitle"
            : "representativeOffers.demand.title",
          { reference: demandId }
        )}
        status={
          <Badge variant={demandStatusBadgeVariant(rollup.status)}>
            {rollup.partiallyAccepted
              ? t("representativeOffers.demand.partiallyAccepted")
              : t(offerStatusLabelKey(rollup.status))}
          </Badge>
        }
        updatedAt={formatOfferDateTime(updatedAt)}
        title={offers.map(offer => getLocalisedName(offer.buy_commodity)).join(", ")}
        subtitle={
          <>
            <AggregatorLine offer={offers[0]} onAggregatorDetails={onAggregatorDetails} />
            {showBuyer && <BuyerLine offer={offers[0]} />}
          </>
        }
        value={valueLabel(grandTotal)}
        valueCaption={
          sharedUnit
            ? t("representativeOffers.card.itemsAndQuantity", {
                count: totals.items,
                quantity: quantityLabel(totals.quantity, sharedUnit),
              })
            : t("representativeOffers.card.items", { count: totals.items })
        }
        trailing={
          <WFPButton
            variant="outline"
            size="icon"
            aria-expanded={open}
            aria-label={t(
              open
                ? "representativeOffers.demand.hideLines"
                : "representativeOffers.demand.showLines",
              { count: totals.items }
            )}
            onClick={() => setOpen(current => !current)}
          >
            <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
          </WFPButton>
        }
      />

      <OfferActionBand
        tone={tone}
        message={offerBriefText(driver, t, currency, briefUnit, deliveryLookups, {
          agreed: quantityLabel(changes.quantity?.from ?? totals.quantity, briefUnit),
          collected: quantityLabel(totals.quantity, briefUnit),
          total: valueLabel(grandTotal),
        })}
        chips={
          (changes.quantity || changes.deliveryCost || changes.value) && (
            <>
              {changes.quantity && (
                <ChangeChip
                  label={t("representativeOffers.card.change.quantity")}
                  tone={tone}
                  move={changes.quantity}
                  format={value => quantityLabel(value, briefUnit)}
                />
              )}
              {changes.deliveryCost && (
                <ChangeChip
                  label={t("representativeOffers.card.change.deliveryCost")}
                  tone={tone}
                  move={changes.deliveryCost}
                  format={valueLabel}
                />
              )}
              {changes.value && (
                <ChangeChip
                  label={t("representativeOffers.card.change.value")}
                  tone={tone}
                  move={changes.value}
                  format={valueLabel}
                />
              )}
            </>
          )
        }
        actions={
          <>
            {/* An aggregator proposal waits on the buyer, so it is reachable without expanding. */}
            {demandCostState(costTerms) === "awaitingYou" && (
              <ReviewDeliveryCostButton
                terms={costTerms}
                onReviewDeliveryCost={onReviewDeliveryCost}
              />
            )}
            <OfferActionButton offer={driver} onViewComments={onViewComments} />
          </>
        }
      />

      {open && (
        <div className="border-border border-t">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="px-4 uppercase">
                  {t("representativeOffers.card.items", { count: totals.items })}
                </TableHead>
                <TableHead className="px-4 uppercase">
                  {t("marketOfferView.details.columns.offerQuantity")}
                </TableHead>
                <TableHead className="px-4 uppercase">
                  {t("marketOfferView.details.columns.stockUnitPrice")}
                </TableHead>
                <TableHead className="px-4 text-right uppercase">
                  {t("representativeOffers.card.itemValue")}
                </TableHead>
                <TableHead className="px-4" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {offers.map(offer => {
                const terms = offerTerms(offer);
                const baseline = offerBaselineTerms(offer);
                const declined =
                  offer.status === OfferStatus.Rejected ||
                  offer.status === OfferStatus.Recalled ||
                  offer.status === OfferStatus.ExchangeCanceled;
                const lineStatus =
                  offer.delivery_confirmation || offer.status === OfferStatus.ExchangeCanceled
                    ? offerDisplayStatus(offer, readOnly)
                    : null;
                const rowUnit = offer.unit_of_measure?.code ?? "";
                const deposit =
                  offer.offer_type === OFFER_TYPES.DIRECT_OFFER ? offer.deposit : undefined;

                return (
                  <TableRow key={offer.id} className={cn(declined && "text-muted-foreground")}>
                    <TableCell className="px-4 py-3 align-top">
                      <span className="flex gap-3">
                        <Carousel
                          className="size-16 shrink-0"
                          imageUrls={offerPictureUrls(offer)}
                        />
                        <span className="flex flex-col gap-0.5">
                          <span className="font-semibold text-foreground">
                            {offerCommodityTitle(offer, schoolDemand)}
                          </span>
                          {deposit && (
                            <span className="text-muted-foreground text-xs">
                              {t("representativeOffers.card.collectedAt", {
                                quantity: quantityLabel(
                                  Number(deposit.original_quantity ?? deposit.quantity),
                                  rowUnit
                                ),
                                price: formatUnitPrice(deposit.price, currency, rowUnit),
                              })}
                            </span>
                          )}
                          {lineStatus ? (
                            <Badge variant={lineStatus.variant} className="mt-1">
                              {t(lineStatus.labelKey, lineStatus.params)}
                            </Badge>
                          ) : (
                            declined && (
                              <Badge variant="destructive" className="mt-1">
                                {t(offerStatusLabelKey(offer.status))}
                              </Badge>
                            )
                          )}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 align-top">
                      <span className="flex flex-col gap-1">
                        {`${formatQuantityRange(terms.minimumQuantity, terms.maximumQuantity)} ${rowUnit}`}
                        {terms.minimumQuantity !== baseline.minimumQuantity &&
                          wasChip(quantityLabel(baseline.minimumQuantity, rowUnit))}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 align-top">
                      <span className="flex flex-col gap-1">
                        {formatUnitPrice(terms.price, currency, rowUnit)}
                        {terms.price !== baseline.price &&
                          wasChip(formatUnitPrice(baseline.price, currency, rowUnit))}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right align-top font-semibold">
                      {valueLabel(termsValue(terms))}
                    </TableCell>
                    <TableCell className="px-4 py-3 align-top">
                      <span className="flex justify-end gap-2">
                        {!hideRecall && !schoolDemand && canRecallOffer(offer.status) && (
                          <WFPButton
                            variant="destructive-secondary"
                            size="sm"
                            onClick={() => onRecall(offer)}
                          >
                            {t("offerCard.recallOffer")}
                          </WFPButton>
                        )}
                        <DeliveryActionButtons
                          offer={offer}
                          readOnly={readOnly}
                          onDeliveryAction={onDeliveryAction}
                          size="sm"
                        />
                        <OfferActionButton
                          offer={offer}
                          onViewComments={onViewComments}
                          size="sm"
                        />
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              {showDeliveryCost && costTerms && (
                <TableRow className="hover:bg-transparent">
                  <TableCell className="px-4 py-3">
                    <span className="flex flex-wrap items-center gap-2">
                      {t("representativeOffers.card.deliveryCost")}
                      <DemandCostBadge terms={costTerms} offers={offers} />
                    </span>
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="px-4 py-3 text-right">
                    {hasDeliveryCost(latestDemandDeliveryCost(costTerms))
                      ? valueLabel(deliveryCostAmount(latestDemandDeliveryCost(costTerms)))
                      : t("termsComparison.notSpecified")}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <span className="flex justify-end">
                      <ReviewDeliveryCostButton
                        terms={costTerms}
                        onReviewDeliveryCost={onReviewDeliveryCost}
                        size="sm"
                      />
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {transportCost > 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell className="px-4 py-3">
                    {t("representativeOffers.card.transportCost")}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="px-4 py-3 text-right">
                    {valueLabel(transportCost)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
              <TableRow className="hover:bg-transparent">
                <TableCell className="px-4 py-3 font-semibold uppercase">
                  {t("representativeOffers.card.offerTotal")}
                </TableCell>
                <TableCell className="px-4 py-3 font-semibold">
                  {sharedUnit ? quantityLabel(totals.quantity, sharedUnit) : "—"}
                </TableCell>
                <TableCell />
                <TableCell className="px-4 py-3 text-right font-semibold">
                  {valueLabel(grandTotal)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      )}
    </Card>
  );
};
